import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { from, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { RedirectRequest, PopupRequest } from '@azure/msal-browser';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class MfaService {

  public lastMfaTime: Date | null = null;
  private readonly MFA_TIMEOUT = environment.mfaTimeOutSeconds || 600;

  constructor(private msalService: MsalService) {}

  /**
   * Performs MFA if necessary. Returns Observable<boolean>
   * indicating success or failure.
   */
  doMfa(): Observable<boolean> {
    const timeSinceLastMfa = this.calculateTimeSinceLastMfa();

    // If MFA done recently (within 10 min), skip
    if (timeSinceLastMfa !== null && timeSinceLastMfa < this.MFA_TIMEOUT) {
      console.log('MFA recently verified, skipping new prompt.');
      return of(true);
    }

    return this.triggerMfa().pipe(
      tap(() => this.lastMfaTime = new Date()), // update time on success
      map(() => true),
      catchError(err => {
        console.error('MFA failed or cancelled:', err);
        return of(false);
      })
    );
  }

  /**
   * Actually triggers MFA popup
   */
  private triggerMfa(): Observable<void> {
    const mfaRequest: RedirectRequest | PopupRequest = {
      scopes: ['openid', 'profile'],
      authority: environment.b2cPolicies.authorities.mfa.authority,
      redirectUri: window.location.origin + '/', // return to profile
      nonce: 'defaultNonce',
    };

    return this.msalService.loginPopup(mfaRequest).pipe(map(() => {}));
  }

  /**
   * Check if current account has MFA (amr) claim
   */
  hasAmrClaim(): boolean {
    const account = this.msalService.instance.getActiveAccount();
    return account?.idTokenClaims?.amr?.includes('mfa') ?? false;
  }

  /**
   * Calculate seconds since last MFA
   */
  private calculateTimeSinceLastMfa(): number | null {
    if (!this.lastMfaTime) {
      return null;
    }

    const now = new Date();
    const diffInMs = now.getTime() - this.lastMfaTime.getTime();
    return diffInMs / 1000; // return time in seconds
  }
}
